import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Lightbulb, Loader2, MessageSquare, XCircle, Tag, TrendingUp, Wrench } from 'lucide-react';
import { ideasAPI } from '@/lib/api';
import { IdeaStatusBadge } from './IdeaStatusBadge';
import { CATEGORY_LABELS, IMPACT_LABELS, EASE_LABELS, fmtDate } from '@/lib/ideaConstants';

export const MyIdeasList = ({ refreshKey }) => {
    const [ideas, setIdeas] = useState(null);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        ideasAPI.mine().then(({ data }) => setIdeas(data)).catch(() => setIdeas([]));
    }, [refreshKey]);

    if (ideas === null) {
        return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
    }
    if (ideas.length === 0) {
        return (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center" data-testid="my-ideas-empty">
                <Lightbulb className="mx-auto h-10 w-10 text-amber-400" />
                <p className="mt-3 font-semibold text-slate-700">Aucune idée pour le moment</p>
                <p className="mt-1 text-sm text-slate-400">Partagez votre première proposition d'amélioration pour Valnexo.</p>
            </div>
        );
    }

    return (
        <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="my-ideas-list">
                {ideas.map((idea) => (
                    <button key={idea.id} onClick={() => setSelected(idea)} data-testid={`my-idea-${idea.id}`}
                        className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-[0_2px_12px_rgba(11,31,59,0.04)] transition-all hover:border-amber-300 hover:shadow-md">
                        <div className="mb-2 flex items-start justify-between gap-2">
                            <IdeaStatusBadge status={idea.status} />
                            {idea.comments?.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-slate-400"><MessageSquare className="h-3.5 w-3.5" />{idea.comments.length}</span>
                            )}
                        </div>
                        <h3 className="line-clamp-2 font-bold text-slate-900">{idea.title}</h3>
                        <p className="mt-1 inline-flex items-center gap-1 text-xs text-slate-400"><Tag className="h-3 w-3" />{idea.category_label}</p>
                        <p className="mt-2 line-clamp-2 text-sm text-slate-500">{idea.description}</p>
                        {idea.status === 'refusee' && idea.refusal_reason && (
                            <p className="mt-2 line-clamp-1 rounded-lg bg-red-50 px-2 py-1 text-xs text-red-600">Motif : {idea.refusal_reason}</p>
                        )}
                        <p className="mt-3 text-xs text-slate-400">{fmtDate(idea.created_at)}</p>
                    </button>
                ))}
            </div>

            <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
                <DialogContent className="max-w-lg p-0 overflow-hidden gap-0" data-testid="my-idea-detail">
                    <DialogTitle className="sr-only">Détail de mon idée</DialogTitle>
                    {selected && (
                        <div className="flex max-h-[85vh] flex-col">
                            <div className="border-b border-slate-100 px-6 py-4">
                                <IdeaStatusBadge status={selected.status} />
                                <h2 className="mt-2 text-lg font-bold text-slate-900">{selected.title}</h2>
                                <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-slate-400"><Tag className="h-3 w-3" />{selected.category_label} · {fmtDate(selected.created_at)}</p>
                            </div>
                            <div className="space-y-4 overflow-y-auto px-6 py-5 text-sm">
                                <Section title="Description"><p className="whitespace-pre-wrap text-slate-600">{selected.description}</p></Section>
                                {selected.benefits && <Section title="Bénéfices attendus"><p className="whitespace-pre-wrap text-slate-600">{selected.benefits}</p></Section>}
                                <div className="flex gap-4">
                                    <Meta icon={TrendingUp} label="Impact estimé" value={IMPACT_LABELS[selected.impact_estimate]} />
                                    <Meta icon={Wrench} label="Facilité" value={EASE_LABELS[selected.implementation_ease]} />
                                </div>
                                {selected.status === 'refusee' && selected.refusal_reason && (
                                    <div className="rounded-xl bg-red-50 p-3" data-testid="my-idea-refusal">
                                        <p className="flex items-center gap-1.5 text-sm font-semibold text-red-700"><XCircle className="h-4 w-4" /> Idée non retenue</p>
                                        <p className="mt-1 text-sm text-red-600">{selected.refusal_reason}</p>
                                    </div>
                                )}
                                <Section title={`Commentaires de traitement (${selected.comments?.length || 0})`}>
                                    {selected.comments?.length ? (
                                        <div className="space-y-2">
                                            {selected.comments.map((c) => (
                                                <div key={c.id} className="rounded-xl bg-slate-50 p-3">
                                                    <p className="text-xs font-semibold text-slate-700">{c.author_name} · {fmtDate(c.created_at)}</p>
                                                    <p className="mt-0.5 whitespace-pre-wrap text-sm text-slate-600">{c.content}</p>
                                                </div>
                                            ))}
                                        </div>
                                    ) : <p className="text-sm text-slate-400">Aucun commentaire pour l'instant.</p>}
                                </Section>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

const Section = ({ title, children }) => (
    <div><p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">{title}</p>{children}</div>
);
const Meta = ({ icon: Icon, label, value }) => (
    <div className="flex-1 rounded-xl border border-slate-100 p-2.5">
        <p className="flex items-center gap-1 text-xs text-slate-400"><Icon className="h-3 w-3" />{label}</p>
        <p className="mt-0.5 text-sm font-semibold text-slate-700">{value || '—'}</p>
    </div>
);

export default MyIdeasList;
